import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'

/**
 * The horizontal tab strip under a page heading.
 * Spec: docs/specs_20260715_mobile_tablet_sweep.md §4.2 (root cause D)
 *
 * Replaces 11 hand-copied `.pageTabs` blocks that had drifted into three
 * different answers to the same problem:
 *   - WarehousePage: inline `overflowX:'auto'` + per-link `whiteSpace:'nowrap'`
 *     (8 tabs, so it hit the wall first and got patched locally)
 *   - Tickets:       a bespoke `ticket-tabs__more` dropdown
 *   - the other 9:   nothing at all — links squashed to two lines, then were
 *                    clipped away entirely by `.app-content` (up to 364px past
 *                    the edge on a phone; Image 9 of the 2026-07-15 report)
 *
 * Overflow lives in CSS (`.pageTabs` in styles/pages.css), not here: it is
 * purely cosmetic — the same links in the same order, just reachable. Nothing
 * is added or removed by viewport, so there is no reason to involve JS.
 *
 * Desktop renders byte-identical markup to the old copies, which is what keeps
 * AC2 (no regression >=1024px) true by construction rather than by luck.
 *
 * NOTE: Tickets.jsx deliberately keeps its own implementation (spec Q4) — it is
 * flag-proven and folding it in is regression risk for no visible gain.
 */
/**
 * The 11 copies had drifted into three different active-matching rules. They
 * are preserved verbatim rather than unified: which tab lights up is visible
 * behaviour, and collapsing these would change it silently on some pages.
 *
 *   exact   — pathname === path. What 6 of the 8 page shells used.
 *   section — exact, or a child of it. PIM (so /pim/brands/:id keeps Brands lit).
 *   prefix  — startsWith. Deals only (so /deals/part-ex/* keeps Part Ex lit).
 *
 * `section` is the semantically correct one and `prefix` is a looser stand-in
 * for it, but converting Deals is a behaviour change and belongs in its own
 * commit, not smuggled into a layout fix.
 */
const MATCHERS = {
  exact: (pathname, path) => pathname === path,
  section: (pathname, path) => pathname === path || pathname.startsWith(`${path}/`),
  prefix: (pathname, path) => pathname.startsWith(path),
}

function PageTabs({ tabs, match = 'exact', style }) {
  const location = useLocation()
  const isActive = MATCHERS[match]

  return (
    <div className="pageTabs" style={style}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          className={isActive(location.pathname, tab.path) ? 'active' : ''}
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  )
}

export default PageTabs
